/*
 * File: PaymentCancel.jsx
 * Project: lets-talk-web
 */

/**
 * Imports
 */

// Modules
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Utilities
import { createStateLocale } from '../../../utilities/locale';
import { toastError } from '../../../utilities/toaster';

// Components
import { Meta, Typography } from '../../../components';

// Styles
import * as S from './PaymentCancel.styles';

/**
 * Component
 */

const PaymentCancel = ({ meta, locale }) => {
	const navigate = useNavigate();
	const clientLocale = useSelector((state) => state.locale.value);
	const uiMode = useSelector((state) => state.ui.value);
	const stateLocale = createStateLocale(clientLocale, locale);
	const [isStartingCheckout, setIsStartingCheckout] = useState(false);

	const handleReturnToStripeCheckout = async () => {
		setIsStartingCheckout(true);
		try {
			const res = await fetch('/_api/v1/payments/checkout-session', {
				method: 'POST',
				credentials: 'include',
			});
			const data = await res.json();

			if (data?.session?.url) {
				window.location.replace(data.session.url);
				return;
			}
			toastError(uiMode, 'Could not open checkout. Please try again.', { duration: 7000 });
		} catch {
			toastError(uiMode, 'Could not open checkout. Please try again.', { duration: 7000 });
		} finally {
			setIsStartingCheckout(false);
		}
	};

	const handleGoToOnboardingPayment = () => {
		navigate(`${stateLocale.localePath}/join/onboarding?step=11`, { replace: true });
	};

	return (
		<>
			<Meta meta={meta} data={{}} locale={stateLocale} />
			<S.Wrapper>
				<S.Card>
					<S.IconWrapper>
						<S.ErrorCircle>
							<S.ErrorMark>!</S.ErrorMark>
						</S.ErrorCircle>
					</S.IconWrapper>

					<Typography tag="h2" weight="bold">Payment Not Completed</Typography>
					<Typography tag="p" variation="1" weight="regular">
						Your subscription payment was not completed.
						You can return to payment or go to onboarding.
					</Typography>

					<S.ButtonGroup>
						<S.PrimaryActionButton
							isLoading={isStartingCheckout}
							disabled={isStartingCheckout}
							onClick={handleReturnToStripeCheckout}
						>
							Return to Payment
						</S.PrimaryActionButton>
						<S.SecondaryTextButton
							disabled={isStartingCheckout}
							onClick={handleGoToOnboardingPayment}
						>
							Go to Onboarding
						</S.SecondaryTextButton>
					</S.ButtonGroup>
				</S.Card>
			</S.Wrapper>
		</>
	);
};

/**
 * Configuration
 */

PaymentCancel.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape(),
};

PaymentCancel.defaultProps = {
	meta: {},
	locale: {},
};

/**
 * Exports
 */

export default PaymentCancel;
